import { SkyDropdown, SkyInput, SkySwitch } from "@skypointcloud/skypoint-ui";
import { useState, type Ref } from "react";
import type { StepName } from "@shared/types/runs";
import type { RunEventFilterValues } from "@/hooks/use-runs";
import { STEP_LABELS } from "./runFormatting";

/**
 * The run log's filter bar (Session 10, P11; ux-s10.md §1): Level, Step and Connection
 * dropdowns, a Table field and "Oldest first". Round 4 (owner, 2026-09-30): the bar's own
 * "Download events" is gone; the run page has one export, the header's "Export events", which
 * carries these filters while the Events view is open.
 *
 * Every filter goes to the server (`useRunEventsPage`), never over the page already loaded. The
 * controls are in native tab order; a dropdown applies when chosen and the Table field applies on
 * Enter (a form submit), so typing a name does not refetch per keystroke.
 *
 * A composed bar rather than `SkyToolbar`: that component takes button items only (its items are
 * `item | group | divider`), and a toolbar's arrow-key roving focus would fight the dropdowns and
 * the text field. The group is a labelled `<fieldset>` so a screen reader names it.
 */

const LEVEL_OPTIONS = [
  { value: "info", text: "Info" },
  { value: "warn", text: "Warning" },
  { value: "error", text: "Error" },
];

export interface RunEventFiltersProps {
  value: RunEventFilterValues;
  onChange: (next: RunEventFilterValues) => void;
  /** The steps this run has, in chain order; the Step filter is shown only when given. */
  steps?: readonly StepName[];
  /** The run's connections; the Connection filter is shown only for more than one. */
  connections?: ReadonlyArray<{ id: string; name: string }>;
  /** Show the Table field and "Oldest first" (the run page). */
  full?: boolean;
  /**
   * The group, focusable from script only (`tabIndex=-1`): the run page moves focus here after a
   * reader's own "Show events" or "Clear filters" replaces the control they pressed, so focus
   * never falls to the page body. Never moved on a live update.
   */
  ref?: Ref<HTMLFieldSetElement>;
}

export function RunEventFilters({
  value,
  onChange,
  steps,
  connections,
  full = false,
  ref,
}: RunEventFiltersProps) {
  const [tableDraft, setTableDraft] = useState(value.entityName ?? "");
  // The applied table can change from outside (a Tables row's "Show events", "Clear filters"):
  // the field follows it, adjusted during render rather than in an effect.
  const [appliedTable, setAppliedTable] = useState(value.entityName);
  if (appliedTable !== value.entityName) {
    setAppliedTable(value.entityName);
    setTableDraft(value.entityName ?? "");
  }

  const applyTable = (): void => {
    const entityName = tableDraft.trim() || undefined;
    if (entityName !== value.entityName) onChange({ ...value, entityName });
  };

  return (
    <fieldset
      ref={ref}
      aria-label="Filter events"
      tabIndex={-1}
      className="mb-3 flex min-w-0 flex-wrap items-end gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid="run-event-filters"
    >
      <SkyDropdown
        label="Level"
        placeholder="All levels"
        clearable
        options={LEVEL_OPTIONS}
        selectedOptions={value.level ? [value.level] : []}
        onOptionSelect={(values) =>
          onChange({
            ...value,
            level: (values[0] as RunEventFilterValues["level"] | undefined) ?? undefined,
          })
        }
        // Sized like the Runs filters, so the trigger does not shrink to its placeholder in
        // the wrapping row.
        className="w-44"
        testId="run-events-level"
      />
      {steps && steps.length > 0 ? (
        <SkyDropdown
          label="Step"
          placeholder="All steps"
          clearable
          options={steps.map((step) => ({ value: step, text: STEP_LABELS[step] }))}
          selectedOptions={value.step ? [value.step] : []}
          onOptionSelect={(values) =>
            onChange({ ...value, step: (values[0] as StepName | undefined) ?? undefined })
          }
          className="w-44"
          testId="run-events-step"
        />
      ) : null}
      {connections && connections.length > 1 ? (
        <SkyDropdown
          label="Connection"
          placeholder="All connections"
          clearable
          options={connections.map((connection) => ({
            value: connection.id,
            text: connection.name,
          }))}
          selectedOptions={value.dataflowId ? [value.dataflowId] : []}
          onOptionSelect={(values) => onChange({ ...value, dataflowId: values[0] ?? undefined })}
          className="w-60"
          testId="run-events-connection"
        />
      ) : null}
      {full ? (
        <>
          <form
            role="search"
            aria-label="Find a table's events"
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              applyTable();
            }}
            data-testid="run-events-table-form"
          >
            <SkyInput
              label="Table"
              placeholder="Table name, then Enter"
              value={tableDraft}
              onChange={(event) => setTableDraft(event.target.value)}
              clearable
              onClear={() => {
                setTableDraft("");
                if (value.entityName) onChange({ ...value, entityName: undefined });
              }}
              className="w-56"
              testId="run-events-table"
            />
          </form>
          {/* Round 10 (R2.3): a shortcut to Level: Error, not a second filter. */}
          <SkySwitch
            label="Errors only"
            checked={value.level === "error"}
            onCheckedChange={(checked) =>
              onChange({ ...value, level: checked ? "error" : undefined })
            }
            className="pb-2"
            testId="run-events-errors-only"
          />
          <SkySwitch
            label="Show oldest first"
            checked={value.order === "oldest"}
            onCheckedChange={(checked) =>
              onChange({ ...value, order: checked ? "oldest" : undefined })
            }
            className="pb-2"
            testId="run-events-oldest-first"
          />
        </>
      ) : null}
    </fieldset>
  );
}

/** True when any filter narrows the log (the order does not). */
export function hasEventFilters(value: RunEventFilterValues): boolean {
  return Boolean(
    value.level ||
    value.step ||
    value.dataflowId ||
    value.entityName ||
    (value.event && value.event.length > 0),
  );
}
